import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import Calendar from 'react-calendar'
import SvgFragment from '../../../../../fragments/SvgFragment'
import AppTag from '../../../../../fragments/AppTag'
import Checkbox from '../../../../../components/Checkbox'
import ProfilePhoto from '../../../../../components/ProfilePhoto'
import TooltipWrapper from '../../../../../components/TooltipWrapper'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useAuthStore } from '../../../../../stores/auth/useAuthStore'
import { useNotesHandlers } from '../../../../../services/actions/notes/useNotesHandlers'
import { useIsSharedInbox } from '../../../../../stores/inbox/InboxModeContext'
import { ITag } from '../../../../../interfaces/models/connection'

const filterAccent = '#5964ff'
const categoryGray = '#8c96ad'
const valueDark = '#2a2d3a'
const menuText = '#2a2d3a'

interface IDropdownFilterProps {
    label: string
    value: string
    options: { value: string; label: string }[]
    onChange: (value: string) => void
    disabled?: boolean
}

function splitOptionLabel(optionLabel: string, fallbackCategory: string): { category: string; valueText: string } {
    const idx = optionLabel.indexOf(': ')
    if (idx >= 0) {
        return { category: optionLabel.slice(0, idx), valueText: optionLabel.slice(idx + 2) }
    }
    return { category: fallbackCategory, valueText: optionLabel }
}

function menuItemText(optionLabel: string): string {
    const idx = optionLabel.indexOf(': ')
    if (idx >= 0) return optionLabel.slice(idx + 2)
    return optionLabel
}

export const DropdownFilter: React.FC<IDropdownFilterProps> = ({ label, value, options, onChange, disabled }) => {
    const [open, setOpen] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)
    const selected = options.find(o => o.value === value) ?? options[0]
    const { category, valueText } = splitOptionLabel(selected?.label ?? '', label)
    const isNonDefault = value !== options[0]?.value

    useEffect(() => {
        if (!open) return
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [open])

    return (
        <DropdownRoot ref={rootRef}>
            <FilterButton
                type="button"
                disabled={disabled}
                $active={isNonDefault}
                $open={open}
                onClick={() => !disabled && setOpen(o => !o)}
                aria-expanded={open}
                aria-haspopup="listbox"
            >
                <ButtonLabel>
                    <CategoryPart>{category}:</CategoryPart>
                    <ValuePart>{valueText}</ValuePart>
                </ButtonLabel>
                <ChevronWrap>
                    <SvgFragment type="arrowDown" />
                </ChevronWrap>
            </FilterButton>
            {open && !disabled && (
                <Menu role="listbox">
                    {options.map(opt => (
                        <MenuItem
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={opt.value === value}
                            $selected={opt.value === value}
                            onClick={() => {
                                onChange(opt.value)
                                setOpen(false)
                            }}
                        >
                            {menuItemText(opt.label)}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </DropdownRoot>
    )
}

export const PinUnpinFilter: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
    const { store: { pinned }, dispatch } = useInboxStore()
    return (
        <DropdownFilter
            label="Pinned"
            value={pinned}
            options={[
                { value: 'All', label: 'Pinned: All' },
                { value: 'Yes', label: 'Pinned: Yes' },
                { value: 'No', label: 'Pinned: No' },
            ]}
            onChange={v => dispatch({ type: 'set-pinned-filter', payload: v })}
            disabled={disabled}
        />
    )
}

export const StarredFilter: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
    const { store: { starred }, dispatch } = useInboxStore()
    return (
        <DropdownFilter
            label="Starred"
            value={starred}
            options={[
                { value: 'All', label: 'Starred: All' },
                { value: 'Yes', label: 'Starred: Yes' },
                { value: 'No', label: 'Starred: No' },
            ]}
            onChange={v => dispatch({ type: 'set-starred-filter', payload: v })}
            disabled={disabled}
        />
    )
}

export const ReadUnreadFilter: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
    const { store: { readed }, dispatch } = useInboxStore()
    return (
        <DropdownFilter
            label="Read"
            value={readed}
            options={[
                { value: 'All', label: 'Read: All' },
                { value: 'All Read', label: 'Read: Yes' },
                { value: 'All Unread', label: 'Read: No' },
            ]}
            onChange={v => dispatch({ type: 'set-readed-filter', payload: v })}
            disabled={disabled}
        />
    )
}

export const ArchiveFilter: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
    const { store: { archived }, dispatch } = useInboxStore()
    return (
        <DropdownFilter
            label="Archived"
            value={archived}
            options={[
                { value: 'No', label: 'Archived: No' },
                { value: 'Yes', label: 'Archived: Yes' },
                { value: 'All', label: 'Archived: All' },
            ]}
            onChange={v => dispatch({ type: 'set-archived-filter', payload: v })}
            disabled={disabled}
        />
    )
}

// ─── Tag Filter ───────────────────────────────────────────────────────────────

export const TagsFilter: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const rootRef = useRef<HTMLDivElement>(null)
    const { store: { filteringTags, tags }, dispatch } = useInboxStore()

    const options: any[] = (filteringTags as any[])
        .filter((t: any) => !t.isForbidden)
        .sort((a: any, b: any) => (a.title || '').localeCompare(b.title || ''))

    const filtered = search
        ? options.filter((t: any) => t.title?.toLowerCase().includes(search.toLowerCase()))
        : options

    useEffect(() => {
        if (!open) return
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [open])

    const toggleTag = (tag: any) => {
        const selected: any[] = tags as any[]
        const isSelected = selected.some((t: any) => t._id === tag._id)
        dispatch({ type: 'set-tags-filter', payload: isSelected ? selected.filter((t: any) => t._id !== tag._id) : [...selected, tag] })
    }

    const selectedTags: any[] = tags as any[]

    return (
        <DropdownRoot ref={rootRef}>
            <FilterButton type="button" disabled={disabled} $active={selectedTags.length > 0} $open={open}
                onClick={() => !disabled && setOpen(o => !o)}>
                <ButtonLabel>
                    <CategoryPart>Tag: </CategoryPart>
                    <ValuePart>{selectedTags.length ? `${selectedTags.length} Selected` : 'Show All'}</ValuePart>
                </ButtonLabel>
                {selectedTags.length > 0 ? (
                    <InlineClearBtn type="button" onClick={e => { e.stopPropagation(); dispatch({ type: 'set-tags-filter', payload: [] }) }}>×</InlineClearBtn>
                ) : (
                    <ChevronWrap><SvgFragment type="arrowDown" /></ChevronWrap>
                )}
            </FilterButton>
            {open && !disabled && (
                <TagMenu>
                    <TagSearch
                        type="text"
                        placeholder="Search tags..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        autoFocus
                    />
                    <TagList>
                        {filtered.length === 0 && <NoTagsMsg>No tags found</NoTagsMsg>}
                        {filtered.map((tag: any) => {
                            const isSel = selectedTags.some((t: any) => t._id === tag._id)
                            return (
                                <TagListItem key={tag._id} onClick={() => toggleTag(tag)} $selected={isSel}>
                                    <TagFilterAppTagWrap>
                                        <AppTag
                                            tag={
                                                {
                                                    ...tag,
                                                    title: tag.title ?? '',
                                                    _id: String(tag._id),
                                                    color: tag.color || '#8c96ad',
                                                    isForbidden: !!tag.isForbidden,
                                                } as ITag
                                            }
                                        />
                                    </TagFilterAppTagWrap>
                                    {isSel && <TagCheck>✓</TagCheck>}
                                </TagListItem>
                            )
                        })}
                    </TagList>
                </TagMenu>
            )}
        </DropdownRoot>
    )
}

// ─── Last Messaged Filter ─────────────────────────────────────────────────────

const PRESETS = [
    { label: 'Today', from: () => moment().startOf('day'), to: () => moment().endOf('day') },
    { label: 'Yesterday', from: () => moment().subtract(1, 'day').startOf('day'), to: () => moment().subtract(1, 'day').endOf('day') },
    { label: 'This week', from: () => moment().startOf('week'), to: () => moment().endOf('week') },
    { label: 'Last week', from: () => moment().subtract(1, 'week').startOf('week'), to: () => moment().subtract(1, 'week').endOf('week') },
    { label: 'This month', from: () => moment().startOf('month'), to: () => moment().endOf('month') },
    { label: 'Last month', from: () => moment().subtract(1, 'month').startOf('month'), to: () => moment().subtract(1, 'month').endOf('month') },
]

const LM_STATUS_OPTIONS = [
    { value: 'all', label: 'All conversations' },
    { value: 'conversationsStarted', label: 'Conversation started' },
    { value: 'lastMessageOutgoing', label: 'Last message outgoing' },
    { value: 'lastMessageIncoming', label: 'Last message incoming' },
    { value: 'noConversationRecorded', label: 'No conversation recorded' },
] as const

type InboxOwnerRow = { id: string; value: string; selected: boolean; object?: unknown }

function toggleInboxOwnerRows(rows: InboxOwnerRow[], item: InboxOwnerRow): InboxOwnerRow[] {
    if (item.id === 'all') {
        const on = !item.selected
        return rows.map(o => ({ ...o, selected: on }))
    }
    return rows.map(o => {
        if (o.id === item.id) return { ...o, selected: !o.selected }
        if (o.id === 'all') return { ...o, selected: false }
        return o
    })
}

function inboxOwnerPayload(rows: InboxOwnerRow[]): any[] | undefined {
    const picked = rows.filter(o => o.selected && o.id !== 'all')
    if (!picked.length) return undefined
    return picked.map(o => ({ id: o.id, value: o.value, object: o.object }))
}

export const LastMessagedFilter: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
    const [open, setOpen] = useState(false)
    const [statusMenuOpen, setStatusMenuOpen] = useState(false)
    const [ownerMenuOpen, setOwnerMenuOpen] = useState(false)
    const [workspaceUsers, setWorkspaceUsers] = useState<any[]>([])
    const [ownerOptions, setOwnerOptions] = useState<InboxOwnerRow[]>([])
    const rootRef = useRef<HTMLDivElement>(null)
    const statusRef = useRef<HTMLDivElement>(null)
    const ownerRef = useRef<HTMLDivElement>(null)
    const statusMenuRef = useRef<HTMLDivElement>(null)
    const ownerMenuRef = useRef<HTMLDivElement>(null)
    const { store: { workspace } } = useAuthStore()
    const { workspaceUsersPublicAllHandler } = useNotesHandlers()
    const {
        store: { lastMessagedFrom, lastMessagedTo, conversationStatus, lastMessagedOnlyLast, conversationOwners },
        dispatch,
    } = useInboxStore()

    // Shared inbox rows belong to LinkedIn accounts, not members — the account picker narrows those.
    const isSharedInbox = useIsSharedInbox()
    const membersEnabled = !isSharedInbox && !!(workspace?.isBusiness && !workspace?.isStarter)

    const dateFrom = lastMessagedFrom ? moment(lastMessagedFrom).toDate() : null
    const dateTo = lastMessagedTo ? moment(lastMessagedTo).toDate() : null

    useEffect(() => {
        if (!open) return
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false)
                setStatusMenuOpen(false)
                setOwnerMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [open])

    // Close sub-dropdowns on outside click. Use capture phase so we still see events when LMMenu
    // stops propagation (same idea as Contacts DropdownMenu autoClose='outside').
    useEffect(() => {
        if (!statusMenuOpen) return
        const onDoc = (e: MouseEvent) => {
            if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusMenuOpen(false)
        }
        document.addEventListener('mousedown', onDoc, true)
        return () => document.removeEventListener('mousedown', onDoc, true)
    }, [statusMenuOpen])

    useEffect(() => {
        if (!ownerMenuOpen) return
        const onDoc = (e: MouseEvent) => {
            if (ownerRef.current && !ownerRef.current.contains(e.target as Node)) setOwnerMenuOpen(false)
        }
        document.addEventListener('mousedown', onDoc, true)
        return () => document.removeEventListener('mousedown', onDoc, true)
    }, [ownerMenuOpen])

    useEffect(() => {
        if (!open || !membersEnabled) return
        let cancelled = false
        ;(async () => {
            try {
                const result = await workspaceUsersPublicAllHandler({
                    condition: 'or',
                    name: '',
                    email: '',
                    getAll: 'yes',
                })
                if (!cancelled && Array.isArray(result)) setWorkspaceUsers(result)
            } catch {
                if (!cancelled) setWorkspaceUsers([])
            }
        })()
        return () => {
            cancelled = true
        }
        // Intentionally omit workspaceUsersPublicAllHandler — stable enough for open/members gating.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, membersEnabled])

    useEffect(() => {
        if (!open || !membersEnabled || !workspaceUsers.length) return
        setOwnerOptions([
            { id: 'all', value: 'All Members', selected: false, object: {} },
            ...workspaceUsers.map((u: any) => ({
                id: String(u._id),
                value: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Member',
                selected: conversationOwners?.some((o: any) => String(o.id) === String(u._id)) ?? false,
                object: u,
            })),
        ])
    }, [open, membersEnabled, workspaceUsers, conversationOwners])

    const datesActive = !!(lastMessagedFrom || lastMessagedTo)
    const statusActive = conversationStatus?.value !== 'all'
    const ownersActive = Array.isArray(conversationOwners) && conversationOwners.some((o: any) => o?.id !== 'all')
    const isActive = datesActive || statusActive || lastMessagedOnlyLast || ownersActive

    const ownerButtonLabel = (() => {
        if (!membersEnabled) return 'All members'
        if (!conversationOwners?.length) return 'All members'
        const real = conversationOwners.filter((o: any) => o?.id !== 'all')
        if (real.length <= 1) return real[0]?.value || 'All members'
        return `Members: ${real.length} selected`
    })()

    const summaryValue = (() => {
        if (datesActive) {
            return `${lastMessagedFrom ? moment(lastMessagedFrom).format('MMM D') : 'From'} – ${lastMessagedTo ? moment(lastMessagedTo).format('MMM D') : 'To'}`
        }
        if (statusActive) return conversationStatus?.label || 'Status'
        if (ownersActive) {
            const real = (conversationOwners as any[]).filter((o: any) => o?.id !== 'all')
            return real.length <= 1 ? real[0]?.value : `${real.length} members`
        }
        if (lastMessagedOnlyLast) return 'Last messaged only'
        return 'All'
    })()

    const setFrom = (isoOrEmpty: string) => dispatch({ type: 'set-last-messaged-from-filter', payload: isoOrEmpty })
    const setTo = (isoOrEmpty: string) => dispatch({ type: 'set-last-messaged-to-filter', payload: isoOrEmpty })
    const clearAll = () => dispatch({ type: 'reset-last-messaged-filter', payload: undefined })

    const isPresetActive = (p: (typeof PRESETS)[0]) => {
        if (!lastMessagedFrom || !lastMessagedTo) return false
        return moment(lastMessagedFrom).isSame(p.from(), 'minute') && moment(lastMessagedTo).isSame(p.to(), 'minute')
    }

    /** Same pattern as LastMessagedFilterFragment: returnValue="range" without selectRange → each click yields [dayStart, dayEnd]. */
    const handleCalendarFromChange = (v: unknown) => {
        const start = Array.isArray(v) ? v[0] : v instanceof Date ? v : null
        if (!start) {
            setFrom('')
            return
        }
        setFrom(moment(start).startOf('day').toISOString())
    }

    const handleCalendarToChange = (v: unknown) => {
        const end = Array.isArray(v) ? v[1] : v instanceof Date ? v : null
        if (!end) {
            setTo('')
            return
        }
        setTo(moment(end).endOf('day').toISOString())
    }

    const selectedStatus = LM_STATUS_OPTIONS.find(o => o.value === conversationStatus?.value) ?? LM_STATUS_OPTIONS[0]

    useEffect(() => {
        if (!statusMenuOpen || !statusMenuRef.current) return
        const items = statusMenuRef.current.querySelectorAll<HTMLButtonElement>('button[role="option"]')
        const selectedIdx = LM_STATUS_OPTIONS.findIndex(o => o.value === conversationStatus?.value)
        items[selectedIdx >= 0 ? selectedIdx : 0]?.focus()
    }, [statusMenuOpen])

    useEffect(() => {
        if (!ownerMenuOpen || !ownerMenuRef.current) return
        ownerMenuRef.current.querySelectorAll<HTMLButtonElement>('button')[0]?.focus()
    }, [ownerMenuOpen])

    const handleStatusMenuKeyDown = (e: React.KeyboardEvent) => {
        if (!statusMenuRef.current) return
        const items = Array.from(statusMenuRef.current.querySelectorAll<HTMLButtonElement>('button[role="option"]'))
        const idx = items.indexOf(document.activeElement as HTMLButtonElement)
        if (e.key === 'ArrowDown') { e.preventDefault(); items[(idx + 1) % items.length]?.focus() }
        else if (e.key === 'ArrowUp') { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus() }
        else if (e.key === 'Escape') { e.preventDefault(); setStatusMenuOpen(false) }
    }

    const handleOwnerMenuKeyDown = (e: React.KeyboardEvent) => {
        if (!ownerMenuRef.current) return
        const items = Array.from(ownerMenuRef.current.querySelectorAll<HTMLButtonElement>('button'))
        const idx = items.indexOf(document.activeElement as HTMLButtonElement)
        if (e.key === 'ArrowDown') { e.preventDefault(); items[(idx + 1) % items.length]?.focus() }
        else if (e.key === 'ArrowUp') { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus() }
        else if (e.key === 'Escape') { e.preventDefault(); setOwnerMenuOpen(false) }
    }

    const onOwnerRowClick = (e: React.MouseEvent, item: InboxOwnerRow) => {
        e.stopPropagation()
        if (!ownerOptions.length) return
        const next = toggleInboxOwnerRows(ownerOptions, item)
        setOwnerOptions(next)
        dispatch({ type: 'set-conversation-owners-filter', payload: inboxOwnerPayload(next) })
    }

    return (
        <DropdownRoot ref={rootRef} style={{ minWidth: 160 }}>
            <FilterButton
                type="button"
                disabled={disabled}
                $active={isActive}
                $open={open}
                onClick={() => !disabled && setOpen(o => !o)}
            >
                <ButtonLabel>
                    <CategoryPart>Last Messaged: </CategoryPart>
                    <ValuePart>{summaryValue}</ValuePart>
                </ButtonLabel>
                {isActive ? (
                    <InlineClearBtn
                        type="button"
                        onClick={e => {
                            e.stopPropagation()
                            clearAll()
                        }}
                    >
                        ×
                    </InlineClearBtn>
                ) : (
                    <ChevronWrap>
                        <SvgFragment type="arrowDown" />
                    </ChevronWrap>
                )}
            </FilterButton>
            {open && !disabled && (
                <LMMenu onMouseDown={e => e.stopPropagation()}>
                    <PresetsRow>
                        {PRESETS.map(p => (
                            <PresetBtn
                                key={p.label}
                                type="button"
                                $active={isPresetActive(p)}
                                onClick={() => {
                                    setFrom(p.from().toISOString())
                                    setTo(p.to().toISOString())
                                }}
                            >
                                {p.label}
                            </PresetBtn>
                        ))}
                    </PresetsRow>
                    <LMSep />
                    <ToggleRow>
                        <ToggleLabel>Show last messaged only</ToggleLabel>
                        <ToggleSwitch
                            type="button"
                            role="switch"
                            aria-checked={lastMessagedOnlyLast}
                            $on={lastMessagedOnlyLast}
                            onClick={() =>
                                dispatch({
                                    type: 'set-last-messaged-only-last',
                                    payload: !lastMessagedOnlyLast,
                                })
                            }
                        >
                            <ToggleKnob $on={lastMessagedOnlyLast} />
                        </ToggleSwitch>
                    </ToggleRow>
                    <LMSep />
                    <CalRow>
                        <CalCol>
                            <DateColLabel>From Date</DateColLabel>
                            <LMStyledCalendar
                                value={!dateTo ? dateFrom : dateFrom && dateTo ? [dateFrom, dateTo] : dateFrom}
                                onChange={handleCalendarFromChange}
                                showNeighboringMonth={false}
                                minDate={new Date(1900, 4, 5)}
                                maxDate={dateTo || new Date()}
                                returnValue="range"
                                defaultActiveStartDate={
                                    dateFrom || new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)
                                }
                                locale="en-US"
                            />
                        </CalCol>
                        <CalCol>
                            <DateColLabel>To Date</DateColLabel>
                            <LMStyledCalendar
                                value={!dateTo ? null : dateFrom && dateTo ? [dateFrom, dateTo] : null}
                                onChange={handleCalendarToChange}
                                showNeighboringMonth={false}
                                minDate={dateFrom || new Date(1900, 4, 5)}
                                maxDate={new Date(2050, 4, 5)}
                                returnValue="range"
                                defaultActiveStartDate={dateTo || dateFrom || undefined}
                                locale="en-US"
                            />
                        </CalCol>
                    </CalRow>
                    <LMSep />
                    <LMFooterRow>
                        <LMFooterCol ref={statusRef}>
                            <LMFieldLabel>Conversation status</LMFieldLabel>
                            <StatusTrigger
                                type="button"
                                $open={statusMenuOpen}
                                onClick={() => setStatusMenuOpen(s => !s)}
                                onKeyDown={e => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setStatusMenuOpen(true) } }}
                            >
                                <span>{selectedStatus.label}</span>
                                <ChevronWrap>
                                    <SvgFragment type="arrowDown" />
                                </ChevronWrap>
                            </StatusTrigger>
                            {statusMenuOpen && (
                                <StatusMenu role="listbox" ref={statusMenuRef} onKeyDown={handleStatusMenuKeyDown}>
                                    {LM_STATUS_OPTIONS.map(opt => (
                                        <StatusMenuItem
                                            key={opt.value}
                                            type="button"
                                            role="option"
                                            $selected={opt.value === conversationStatus?.value}
                                            onClick={() => {
                                                dispatch({
                                                    type: 'set-conversation-status-filter',
                                                    payload: opt,
                                                })
                                                setStatusMenuOpen(false)
                                            }}
                                        >
                                            {opt.label}
                                        </StatusMenuItem>
                                    ))}
                                </StatusMenu>
                            )}
                        </LMFooterCol>
                        <LMFooterCol ref={ownerRef}>
                            {membersEnabled ? (
                                <>
                                    <LMFieldLabel>Conversation owner</LMFieldLabel>
                                    <StatusTrigger
                                        type="button"
                                        $open={ownerMenuOpen}
                                        onClick={() => setOwnerMenuOpen(s => !s)}
                                        onKeyDown={e => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOwnerMenuOpen(true) } }}
                                    >
                                        <span>{ownerButtonLabel}</span>
                                        <ChevronWrap>
                                            <SvgFragment type="downArrow" />
                                        </ChevronWrap>
                                    </StatusTrigger>
                                    {ownerMenuOpen && ownerOptions.length > 0 && (
                                        <OwnerPickerMenu ref={ownerMenuRef} onKeyDown={handleOwnerMenuKeyDown}>
                                            {ownerOptions.map(opt => (
                                                <OwnerPickerRow
                                                    key={opt.id}
                                                    type="button"
                                                    $selected={opt.selected}
                                                    onClick={e => onOwnerRowClick(e, opt)}
                                                >
                                                    <Checkbox
                                                        checked={opt.selected}
                                                        checkboxId={`inbox-lastmsg-owner-${opt.id}`}
                                                    />
                                                    {opt.id === 'all' ? (
                                                        <OwnerPickerAllLabel>All Members</OwnerPickerAllLabel>
                                                    ) : (
                                                        <>
                                                            <OwnerAvatarWrap>
                                                                <ProfilePhoto
                                                                    capitalLetters={(() => {
                                                                        const u = opt.object as any
                                                                        return `${u?.firstName?.charAt(0) ?? ''}${u?.lastName?.charAt(0) ?? ''}`.toUpperCase() || 'NN'
                                                                    })()}
                                                                    width="32px"
                                                                    height="32px"
                                                                    minWidth="32px"
                                                                    minHeight="32px"
                                                                    tooltipMessage={`${(opt.object as any)?.firstName ?? ''} ${(opt.object as any)?.lastName ?? ''}`.trim()}
                                                                />
                                                            </OwnerAvatarWrap>
                                                            <OwnerMemberMeta>
                                                                <OwnerMemberName>
                                                                    {`${(opt.object as any)?.firstName ?? ''} ${(opt.object as any)?.lastName ?? ''}`.trim() || opt.value}
                                                                </OwnerMemberName>
                                                                <TooltipWrapper tooltipText={(opt.object as any)?.email ?? ''} placement="top">
                                                                    <OwnerMemberEmail>
                                                                        {(opt.object as any)?.email ?? ''}
                                                                    </OwnerMemberEmail>
                                                                </TooltipWrapper>
                                                            </OwnerMemberMeta>
                                                        </>
                                                    )}
                                                </OwnerPickerRow>
                                            ))}
                                        </OwnerPickerMenu>
                                    )}
                                </>
                            ) : (
                                <>
                                    <LMFieldLabel>
                                        Conversation owner{' '}
                                        <LockMuted>
                                            <SvgFragment type="lock2" />
                                        </LockMuted>
                                    </LMFieldLabel>
                                    <OwnerFakeButton type="button" disabled>
                                        All members
                                    </OwnerFakeButton>
                                    <OwnerHint>
                                        {workspace?.isStarter
                                            ? 'Upgrade your plan to unlock this feature.'
                                            : 'Turn on Business workspace to filter by conversation owner (same as on Contacts).'}
                                    </OwnerHint>
                                </>
                            )}
                        </LMFooterCol>
                    </LMFooterRow>
                </LMMenu>
            )}
        </DropdownRoot>
    )
}

// ─── Shared styled components ─────────────────────────────────────────────────

const DropdownRoot = styled.div`
    position: relative;
    display: inline-block;
    margin-right: 0;
    vertical-align: top;
`

const FilterButton = styled.button<{ $active: boolean; $open: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: 34px;
    min-width: 0;
    padding: 0 11px 0 13px;
    border-radius: 6px;
    border: 1px solid ${props => (props.$active || props.$open ? filterAccent : '#dae0e8')};
    background: ${props => (props.$active || props.$open ? '#eef3ff' : '#fff')};
    cursor: pointer;
    outline: none;
    font: inherit;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const ButtonLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    font-size: 13px;
    line-height: 15.6px;
`

const CategoryPart = styled.span`
    color: ${categoryGray};
    font-weight: 400;
`

const ValuePart = styled.span`
    color: ${valueDark};
    font-weight: 700;
`

const ChevronWrap = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    opacity: 0.85;

    svg {
        width: 22px;
        height: 22px;
        display: block;
    }
    svg path {
        fill: ${categoryGray};
    }
`

const Menu = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 100%;
    z-index: 1200;
    padding: 6px 0;
    background: #fff;
    border: 1px solid #e5e9ef;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
`

const MenuItem = styled.button<{ $selected: boolean }>`
    display: block;
    width: 100%;
    padding: 10px 14px;
    border: none;
    background: none;
    text-align: left;
    font-size: 13px;
    line-height: 1.35;
    cursor: pointer;
    color: ${props => (props.$selected ? filterAccent : menuText)};
    font-weight: ${props => (props.$selected ? 600 : 400)};

    &:hover {
        background: #f6f8fb;
    }
`

// ─── Tag filter styled components ─────────────────────────────────────────────

const InlineClearBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    background: none;
    border: none;
    font-size: 15px;
    line-height: 1;
    color: ${categoryGray};
    cursor: pointer;
    padding: 0;
    &:hover { color: #ff6760; }
`

const TagMenu = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: 260px;
    z-index: 1200;
    background: #fff;
    border: 1px solid #e5e9ef;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
    padding: 10px;
`

const TagSearch = styled.input`
    width: 100%;
    height: 30px;
    padding: 0 10px;
    border: 1px solid #dae0e8;
    border-radius: 5px;
    font-size: 13px;
    outline: none;
    margin-bottom: 8px;
    &:focus { border-color: ${filterAccent}; }
`

const TagList = styled.div`
    max-height: 200px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const TagListItem = styled.div<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 6px;
    border-radius: 5px;
    cursor: pointer;
    background: ${props => props.$selected ? '#f0f4ff' : 'transparent'};
    &:hover { background: #f6f8fb; }
`

const TagFilterAppTagWrap = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
`

const TagCheck = styled.span`
    font-size: 12px;
    color: ${filterAccent};
    font-weight: 700;
    margin-left: 6px;
`

const NoTagsMsg = styled.div`
    padding: 12px;
    font-size: 13px;
    color: ${categoryGray};
    text-align: center;
`

// ─── Last Messaged filter styled components ────────────────────────────────────

const LMMenu = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: min(552px, calc(100vw - 48px));
    z-index: 1200;
    background: #fff;
    border: 1px solid #e5e9ef;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    padding: 16px;
`

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 4px 0 2px;
`

const ToggleLabel = styled.span`
    font-size: 13px;
    color: ${valueDark};
    font-weight: 600;
`

const ToggleSwitch = styled.button<{ $on: boolean }>`
    position: relative;
    width: 42px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    background: ${p => (p.$on ? filterAccent : '#d0d7e2')};
    transition: background 0.15s ease;
    padding: 0;
    outline: none;

    &:focus-visible {
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.35);
    }
`

const ToggleKnob = styled.span<{ $on: boolean }>`
    position: absolute;
    top: 3px;
    left: ${p => (p.$on ? '22px' : '3px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    transition: left 0.15s ease;
    pointer-events: none;
`

const CalRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    align-items: flex-start;
`

const CalCol = styled.div`
    flex: 0 0 auto;
    width: 240px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const LMStyledCalendar = styled(Calendar)`
    width: 240px;
    font-size: 13px;
    font-weight: 600;
    color: ${valueDark};

    & abbr {
        text-decoration: none;
    }

    & button {
        border: none;
        background-color: transparent;
    }

    & button:disabled,
    & button:disabled:hover {
        color: #b8bfcf;
    }

    & button:not(:disabled) {
        color: #2a2d3a;
        cursor: pointer;
    }

    & .react-calendar__navigation {
        height: 20px;
        margin-bottom: 10px;
        display: flex;
    }

    & .react-calendar__navigation__label {
        font-weight: 700;
        color: ${valueDark};
        display: flex;
        justify-content: center;
    }

    & .react-calendar__navigation__arrow {
        line-height: 20px;
        font-size: 18px;
        font-weight: 700;
        color: ${categoryGray};
    }

    & .react-calendar__viewContainer {
        height: auto;
    }

    & .react-calendar__month-view__weekdays {
        height: 20px;
        margin-bottom: 4px;
        align-items: center;
    }

    & .react-calendar__month-view__weekdays__weekday {
        text-align: center;
        font-size: 11px;
        color: ${categoryGray};
    }

    & .react-calendar__month-view__days {
        height: auto;
    }

    & .react-calendar__tile {
        height: 28px;
        width: 33px;
        margin-bottom: 2px;
        font-size: 12px;
    }

    & .react-calendar__tile:enabled:hover {
        color: ${filterAccent};
    }

    & .react-calendar__tile--active {
        background: #eef3ff;
        color: #2a2d3a !important;
    }

    & .react-calendar__tile--now {
        box-shadow: inset 0 0 0 1px ${filterAccent};
    }

    & .react-calendar__tile--rangeStart,
    & .react-calendar__tile--rangeEnd {
        border-radius: 6px;
        background: ${filterAccent};
        color: #fff !important;

        &:enabled:hover {
            background: #7a89fc;
            color: #fff !important;
        }
    }
`

const LMFooterRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
`

const LMFooterCol = styled.div`
    flex: 1 1 200px;
    min-width: 0;
    position: relative;
`

const LMFieldLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${categoryGray};
    margin-bottom: 8px;
`

const LockMuted = styled.span`
    display: inline-flex;
    opacity: 0.55;
    svg {
        display: block;
        width: 12px;
        height: 14px;
    }
`

const StatusTrigger = styled.button<{ $open: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    height: 36px;
    padding: 0 10px;
    border-radius: 6px;
    border: 1px solid ${p => (p.$open ? filterAccent : '#dae0e8')};
    background: #fff;
    font-size: 13px;
    color: ${valueDark};
    cursor: pointer;
    outline: none;
    text-align: left;

    &:hover {
        border-color: ${filterAccent};
    }
`

const StatusMenu = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% + 4px);
    z-index: 10;
    max-height: 260px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #e5e9ef;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    padding: 4px 0;
`

const StatusMenuItem = styled.button<{ $selected: boolean }>`
    display: block;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: ${p => (p.$selected ? '#f0f4ff' : 'none')};
    text-align: left;
    font-size: 13px;
    color: ${p => (p.$selected ? filterAccent : menuText)};
    font-weight: ${p => (p.$selected ? 600 : 400)};
    cursor: pointer;

    &:hover {
        background: #f6f8fb;
    }
`

const OwnerPickerMenu = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% + 4px);
    z-index: 10;
    max-height: 280px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #e5e9ef;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    padding: 4px 0;
`

const OwnerPickerRow = styled.button<{ $selected?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: ${p => (p.$selected ? '#f0f4ff' : 'none')};
    cursor: pointer;
    text-align: left;
    font-size: 13px;
    color: ${menuText};

    &:hover {
        background: ${p => (p.$selected ? '#e8edff' : '#f6f8fb')};
    }
`

const OwnerPickerAllLabel = styled.span`
    flex: 1;
    font-size: 13px;
    font-weight: 700;
    color: ${filterAccent};
`

const OwnerAvatarWrap = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
`

const OwnerMemberMeta = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-start;
`

const OwnerMemberName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${valueDark};
    line-height: 1.25;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const OwnerMemberEmail = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${categoryGray};
    line-height: 1.25;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const OwnerFakeButton = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    height: 36px;
    padding: 0 10px;
    border-radius: 6px;
    border: 1px solid #e5e9ef;
    background: #f6f8fb;
    font-size: 13px;
    color: ${categoryGray};
    cursor: not-allowed;
    opacity: 0.92;
`

const OwnerHint = styled.p`
    margin: 8px 0 0;
    font-size: 11px;
    line-height: 1.35;
    color: ${categoryGray};
`

const PresetsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 4px;
`

const PresetBtn = styled.button<{ $active: boolean }>`
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid ${props => props.$active ? filterAccent : 'transparent'};
    background: #eef3ff;
    color: ${filterAccent};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    &:hover { border-color: ${filterAccent}; }
`

const LMSep = styled.div`
    height: 1px;
    background: #ebeff4;
    margin: 12px 0;
`

const DateColLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${categoryGray};
`
